import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { DemoStorageService } from '../../core/demo-storage.service';
import type { RegistroCambio, EstadoCambio, NivelRiesgo } from './models/gestion-cambio.model';

const STORAGE_KEY = 'gestion_cambios';

function demoCambios(): RegistroCambio[] {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const d = (daysAgo: number) =>
    new Date(Date.now() - daysAgo * 24 * 3600 * 1000).toISOString().slice(0, 10);
  return [
    {
      id: '1',
      titulo: 'Nueva línea de producción L3',
      tipo: 'equipo',
      descripcion: 'Instalación de nueva línea con maquinaria adicional.',
      areaAfectada: 'Producción',
      fechaRegistro: d(30),
      solicitante: 'Juan Pérez',
      estado: 'aprobado',
      nivelRiesgo: 'medio',
      controlesPreventivos: 'Capacitación previa, EPP específico, procedimiento LOTO actualizado.',
      aprobadoPor: 'María García',
      fechaAprobacion: d(25),
      observaciones: '',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '2',
      titulo: 'Cambio de turnos en Almacén',
      tipo: 'personal',
      descripcion: 'Reorganización de turnos para cubrir demanda.',
      areaAfectada: 'Almacén',
      fechaRegistro: d(5),
      solicitante: 'Ana López',
      estado: 'evaluacion',
      observaciones: 'Pendiente evaluación de fatiga y transporte.',
      createdAt: now,
      updatedAt: now,
    },
  ];
}

@Injectable({ providedIn: 'root' })
export class GestionCambioService {
  private cambios: RegistroCambio[] = demoCambios();

  constructor(private demoStorage: DemoStorageService) {}

  async loadFromDemoStorage(): Promise<void> {
    if (!environment.demo) return;
    const data = await this.demoStorage.get<RegistroCambio[]>(STORAGE_KEY);
    this.cambios = Array.isArray(data) ? data : demoCambios();
    await this.persistDemo();
  }

  private persistDemo(): Promise<void> {
    if (!environment.demo) return Promise.resolve();
    return this.demoStorage.set(STORAGE_KEY, this.cambios).catch(() => {});
  }

  getCambios(filters?: { estado?: string; tipo?: string }): RegistroCambio[] {
    let list = [...this.cambios];
    if (filters?.estado) list = list.filter((c) => c.estado === filters.estado);
    if (filters?.tipo) list = list.filter((c) => c.tipo === filters.tipo);
    return list.sort((a, b) => b.fechaRegistro.localeCompare(a.fechaRegistro));
  }

  getById(id: string): RegistroCambio | null {
    return this.cambios.find((c) => c.id === id) ?? null;
  }

  createCambio(data: Omit<RegistroCambio, 'id' | 'createdAt' | 'updatedAt'>): RegistroCambio {
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const nuevo: RegistroCambio = {
      ...data,
      id: 'ch' + Date.now(),
      createdAt: now,
      updatedAt: now,
    };
    this.cambios.push(nuevo);
    this.persistDemo();
    return { ...nuevo };
  }

  updateCambio(id: string, data: Partial<Omit<RegistroCambio, 'id' | 'createdAt'>>): RegistroCambio | null {
    const c = this.cambios.find((x) => x.id === id);
    if (!c) return null;
    Object.assign(c, data);
    c.updatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.persistDemo();
    return { ...c };
  }

  deleteCambio(id: string): boolean {
    const idx = this.cambios.findIndex((x) => x.id === id);
    if (idx === -1) return false;
    this.cambios.splice(idx, 1);
    this.persistDemo();
    return true;
  }

  enviarAEvaluacion(id: string): RegistroCambio | null {
    const c = this.cambios.find((x) => x.id === id);
    if (!c || c.estado !== 'registrado') return null;
    return this.updateCambio(id, { estado: 'evaluacion' });
  }

  aprobar(id: string, aprobadoPor: string): RegistroCambio | null {
    const c = this.cambios.find((x) => x.id === id);
    if (!c || (c.estado !== 'evaluacion' && c.estado !== 'registrado')) return null;
    const fecha = new Date().toISOString().slice(0, 10);
    return this.updateCambio(id, { estado: 'aprobado', aprobadoPor, fechaAprobacion: fecha });
  }

  rechazar(id: string, observaciones?: string): RegistroCambio | null {
    const c = this.cambios.find((x) => x.id === id);
    if (!c || (c.estado !== 'evaluacion' && c.estado !== 'registrado')) return null;
    return this.updateCambio(id, { estado: 'rechazado', observaciones: observaciones ?? c.observaciones });
  }

  marcarImplementado(id: string, fechaImplementacion?: string): RegistroCambio | null {
    const c = this.cambios.find((x) => x.id === id);
    if (!c || c.estado !== 'aprobado') return null;
    const fecha = fechaImplementacion || new Date().toISOString().slice(0, 10);
    return this.updateCambio(id, { estado: 'implementado', fechaImplementacion: fecha });
  }
}
